/**
 * EngineStateMachine — applies actions to a HandState and produces new states.
 *
 * Per /design/specs/5_game_engine_spec.md §8. Heads-up only; no side pots.
 *
 * The state machine is purely functional: every call returns a new HandState
 * (input is never mutated). Action validation must occur before `applyAction`
 * — calls with illegal actions throw.
 */

import type { Card } from '@/types/card';
import type { PlayerAction } from '@/types/action';
import { nextStreet, opposingActor } from '@/types/action';
import type { ActionRecord, HandState, Stacks } from '@/types/handState';
import { validateAction } from './actionValidator';

// =============================================================================
// Initial state construction
// =============================================================================

export interface CreateHandStateOptions {
  handId: string;
  potType: 'SRP' | '3BP';
  heroPosition: 'IP' | 'OOP';
  blinds: { sb: number; bb: number };
  heroCards: [Card, Card];
  villainCards: [Card, Card];
  flopCards: [Card, Card, Card];
  /** Pot already accumulated from preflop (auto-resolved in V1). */
  startingPot: number;
  /** Stack sizes at start of flop, after preflop investment. */
  startingStacks: Stacks;
}

/**
 * Construct an initial HandState entering the flop. V1 preflop is
 * auto-resolved by the caller (per PRD §8.1); this function takes the
 * post-preflop position and produces the open-flop state.
 *
 * Action goes to the OOP player on the flop.
 */
export function createFlopState(opts: CreateHandStateOptions): HandState {
  const oopActor = opts.heroPosition === 'OOP' ? 'hero' : 'villain';
  return {
    handId: opts.handId,
    street: 'flop',
    toAct: oopActor,
    phase: 'street_open',

    potType: opts.potType,
    heroPosition: opts.heroPosition,
    blinds: opts.blinds,

    heroCards: opts.heroCards,
    villainCards: opts.villainCards,
    board: [...opts.flopCards],

    pot: opts.startingPot,
    stacks: { ...opts.startingStacks },
    invested: {
      hero: 0,
      villain: 0,
    },
    investedThisStreet: { hero: 0, villain: 0 },

    toCall: 0,
    lastRaiseIncrement: 0,
    bettingReopened: true,

    history: [],
  };
}

// =============================================================================
// Action application
// =============================================================================

/**
 * Apply an action to a HandState and return the resulting state. Throws if
 * the action is illegal. Pure / immutable — caller must replace their state
 * reference with the return value.
 */
export function applyAction(
  state: HandState,
  action: PlayerAction,
): HandState {
  const err = validateAction(state, action);
  if (err) {
    throw new Error(`Invalid action ${JSON.stringify(action)}: ${err.reason}`);
  }

  const actor = state.toAct!;
  const opponent = opposingActor(actor);

  // Compute chip commitment from this action
  const { chipsCommitted, newInvestedThisStreet, newRaiseIncrement } =
    computeChipChange(state, action);

  // Apply chip changes
  const newPot = state.pot + chipsCommitted;
  const newStacks: Stacks = { ...state.stacks };
  newStacks[actor] -= chipsCommitted;

  const newInvested: Stacks = { ...state.invested };
  newInvested[actor] += chipsCommitted;

  const newInvestedThisStreetMap: Stacks = { ...state.investedThisStreet };
  newInvestedThisStreetMap[actor] = newInvestedThisStreet;

  // Compute new toCall for opponent
  const opponentInvestedThisStreet = newInvestedThisStreetMap[opponent];
  const newBetLevel = Math.max(
    newInvestedThisStreetMap.hero,
    newInvestedThisStreetMap.villain,
  );
  const newToCall = newBetLevel - opponentInvestedThisStreet;

  // Determine new lastRaiseIncrement + bettingReopened
  let lastRaiseIncrement = state.lastRaiseIncrement;
  let bettingReopened = state.bettingReopened;
  if (action.kind === 'bet' || action.kind === 'raise') {
    lastRaiseIncrement = newRaiseIncrement;
    // Normal bet/raise reopens betting for the opponent
    bettingReopened = true;
  } else if (action.kind === 'allin') {
    // All-in below min-raise → betting does NOT reopen for prior aggressor
    if (newRaiseIncrement > 0) {
      // The all-in raised the bet level
      const minRaiseSize = Math.max(state.lastRaiseIncrement, state.blinds.bb);
      if (newRaiseIncrement < minRaiseSize) {
        bettingReopened = false;
      } else {
        bettingReopened = true;
        lastRaiseIncrement = newRaiseIncrement;
      }
    }
    // else: all-in matched current bet exactly (just calling all-in) — no
    // change to bettingReopened or lastRaiseIncrement.
  }

  // Record the action
  const record: ActionRecord = {
    index: state.history.length,
    street: state.street,
    actor,
    action,
    chipsCommitted,
    potAfter: newPot,
    stacksAfter: newStacks,
    toCallAfter: newToCall,
  };

  // Determine next phase / toAct
  let nextPhase = state.phase;
  let nextToAct = state.toAct;
  let terminalReason = state.terminalReason;

  if (action.kind === 'fold') {
    nextPhase = 'terminated';
    nextToAct = null;
    terminalReason = 'fold';
  } else if (newStacks.hero === 0 && newStacks.villain === 0) {
    // Both all-in — runout (advance streets without action) → terminal
    nextPhase = 'terminated';
    nextToAct = null;
    terminalReason = 'allin_runout';
  } else if (isRoundClosed(state, action, newToCall)) {
    // Round closed; advance to next street or terminate if river
    nextPhase = 'street_closed';
    nextToAct = null;
  } else {
    // Action continues — opponent's turn
    nextPhase = 'street_open';
    nextToAct = opponent;
  }

  return {
    ...state,
    toAct: nextToAct,
    phase: nextPhase,
    terminalReason,
    pot: newPot,
    stacks: newStacks,
    invested: newInvested,
    investedThisStreet: newInvestedThisStreetMap,
    toCall: newToCall,
    lastRaiseIncrement,
    bettingReopened,
    history: [...state.history, record],
  };
}

// =============================================================================
// Street advancement
// =============================================================================

/**
 * Advance from `street_closed` to the next street's open state. Throws if
 * the current phase isn't street_closed or if there's no next street.
 * Caller provides the new board card(s).
 */
export function advanceStreet(state: HandState, newBoardCards: Card[]): HandState {
  if (state.phase !== 'street_closed') {
    throw new Error(`advanceStreet: phase must be street_closed, got ${state.phase}`);
  }
  const next = nextStreet(state.street);
  if (next === null) {
    throw new Error(`advanceStreet: already on river`);
  }

  // OOP acts first on each new postflop street (same as flop).
  const oopActor = state.heroPosition === 'OOP' ? 'hero' : 'villain';

  return {
    ...state,
    street: next,
    toAct: oopActor,
    phase: 'street_open',
    board: [...state.board, ...newBoardCards],
    investedThisStreet: { hero: 0, villain: 0 },
    toCall: 0,
    lastRaiseIncrement: 0,
    bettingReopened: true,
  };
}

/**
 * Convenience: after the river closes, transition to terminated_showdown.
 * Showdown evaluation itself is deferred to subslice 3b.
 */
export function transitionToShowdown(state: HandState): HandState {
  if (state.phase !== 'street_closed') {
    throw new Error(`transitionToShowdown: phase must be street_closed, got ${state.phase}`);
  }
  if (state.street !== 'river') {
    throw new Error(`transitionToShowdown: street must be river, got ${state.street}`);
  }
  return {
    ...state,
    toAct: null,
    phase: 'terminated',
    terminalReason: 'showdown',
  };
}

// =============================================================================
// Terminal detection
// =============================================================================

export function isTerminal(state: HandState): boolean {
  return state.phase === 'terminated';
}

// =============================================================================
// Internal helpers
// =============================================================================

interface ChipChange {
  chipsCommitted: number;
  newInvestedThisStreet: number;
  /** For bet/raise/allin: new "increment" relative to previous bet level. */
  newRaiseIncrement: number;
}

function computeChipChange(state: HandState, action: PlayerAction): ChipChange {
  const actor = state.toAct!;
  const stack = state.stacks[actor];
  const investedThisStreet = state.investedThisStreet[actor];
  const toCall = state.toCall;
  const previousBetLevel = investedThisStreet + toCall;

  switch (action.kind) {
    case 'fold':
    case 'check':
      return {
        chipsCommitted: 0,
        newInvestedThisStreet: investedThisStreet,
        newRaiseIncrement: 0,
      };

    case 'call':
      return {
        chipsCommitted: toCall,
        newInvestedThisStreet: investedThisStreet + toCall,
        newRaiseIncrement: 0,
      };

    case 'bet':
    case 'raise': {
      const committed = action.total - investedThisStreet;
      return {
        chipsCommitted: committed,
        newInvestedThisStreet: action.total,
        newRaiseIncrement: action.total - previousBetLevel,
      };
    }

    case 'allin': {
      const newInvested = investedThisStreet + stack;
      const increment = Math.max(0, newInvested - previousBetLevel);
      return {
        chipsCommitted: stack,
        newInvestedThisStreet: newInvested,
        newRaiseIncrement: increment,
      };
    }
  }
}

/**
 * True when the just-applied action closes the current betting round.
 *
 * Heads-up closure rules:
 *   - Fold: handled separately (terminal).
 *   - All-in by both: handled separately (allin_runout terminal).
 *   - Call that matches the bet (newToCall === 0) AND prior history has an
 *     aggressive action this street → closes.
 *   - Check by both players this street → closes.
 */
function isRoundClosed(
  beforeState: HandState,
  action: PlayerAction,
  newToCall: number,
): boolean {
  if (action.kind === 'fold') return false; // handled by terminal branch

  // After the action, if opponent owes 0 AND opponent has acted on this street
  // (or the action is a passive close like both-check), the round is closed.
  if (newToCall !== 0) return false;

  const actor = beforeState.toAct!;
  const opponent = opposingActor(actor);

  // Has the opponent acted on this street?
  const opponentActedThisStreet = beforeState.history.some(
    (r) => r.street === beforeState.street && r.actor === opponent,
  );

  // Case 1: call (or all-in-call) — closes if opponent has acted (i.e. opponent
  // made the bet/raise we're calling).
  if (action.kind === 'call') {
    return opponentActedThisStreet;
  }

  // Case 2: check — closes only if opponent has also already checked this street.
  if (action.kind === 'check') {
    return opponentActedThisStreet;
  }

  // Case 3: allin that exactly matches existing bet level (newToCall === 0).
  // If opponent has acted this street → closing all-in-call. Otherwise → first
  // action of street (rare, requires very deep stacks; treat as opens betting).
  if (action.kind === 'allin') {
    return opponentActedThisStreet;
  }

  // bet / raise — opponent hasn't matched yet; can't close.
  return false;
}
